import { reportChatError } from "../reportChatError"
import { buildRetractionMessage } from "./stanzas/message/retraction/build"
import { buildEditMessage } from "./stanzas/message/edit/build"
import { loadHistory } from "./history"
import {
  buildAttachment,
  buildBody,
  buildMessage,
  buildShare,
} from "./stanzas/message/build"
import { nanoid } from "nanoid"
import { jidParts, type Jid } from "./jid"
import { xml, client, type Client } from "@xmpp/client"
import { NS_CARBONS, NS_PUSH, PUSH_NODE } from "./constants"
import type { ChatMessage, ChatState, ConnState, SharedItemRef } from "../types"
import type { MamPage, MamPagingOptions } from "./history"
import { buildChatState } from "./stanzas/chatstate"
import { routeStanza, type StanzaHandlers } from "./stanzas/router"

type XmppClientConfig = {
  /** `${handle}@${companyNameKey}` — the localpart is the opaque handle. */
  jid: Jid
  /** Costanza session token, used as the SASL PLAIN password. */
  sessionToken: string
  /** `wss://…/ws` */
  wsUrl: string
}

export type XmppClientCallbacks = {
  onConnStateChange: (state: ConnState) => void
  /** The token was rejected. This client is done; re-auth and build a new one. */
  onAuthFailed: () => void
  /** Carbons never enabled, so this tab will not see its own sends elsewhere. */
  onCarbonsUnavailable?: () => void
  /** Session is live — the seam for history catch-up. */
  onOnline: (jid: Jid) => void | Promise<void>
} & StanzaHandlers

export type XmppClient = {
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  /**
   * The caller supplies the origin-id so it can put the message on screen before
   * this resolves (optimistic send) — the same id keys the echo and the archive
   * copy, so the two dedupe against the one row. An empty body sends no
   * `<body>`, for an image-only message.
   */
  sendMessage: (peer: Jid, message: ChatMessage) => Promise<void>
  /**
   * Share a learning item (FAME-237): a `<share>` reference plus a deep-link
   * `body` fallback. Returns the origin-id it minted.
   */
  sendShare: (peer: Jid, ref: SharedItemRef, body: string) => Promise<string>
  retract: (peer: Jid, targetId: string, bounceId: string) => Promise<void>
  edit: (
    peer: Jid,
    targetId: string,
    body: string,
    bounceId: string
  ) => Promise<void>
  /** XEP-0085: tell the peer what we are doing in the composer. Fire-and-forget. */
  sendChatState: (peer: Jid, state: ChatState) => void
  loadHistory: (peer: Jid, opts?: MamPagingOptions) => Promise<MamPage>
  /**
   * XEP-0357: register (`true`) or deregister (`false`) this session for push.
   * Fire-and-forget — the coordinator that owns the enable/disable decision has
   * nothing to await. Only meaningful while online.
   */
  setPushEnabled: (enabled: boolean) => void
}

const ACK_INTERVAL_MS = 15_000
const ACK_TIMEOUT_MS = 15_000
const CARBONS_TIMEOUT_MS = 5_000
const PUSH_TIMEOUT_MS = 5_000
const FATAL_STREAM_CONDITIONS = new Set([
  "host-unknown",
  "policy-violation",
  "not-well-formed",
  "unsupported-version",
])

export function createXmppClient(
  config: XmppClientConfig,
  callbacks: XmppClientCallbacks
): XmppClient {
  const { local, domain } = jidParts(config.jid)

  const xmpp = client({
    service: config.wsUrl,
    domain,
    username: local,
    password: config.sessionToken,
    resource: `engage-web-${nanoid(8)}`,
  })

  xmpp.streamManagement.requestAckInterval = ACK_INTERVAL_MS
  xmpp.streamManagement.timeout = ACK_TIMEOUT_MS

  let DONE = false
  const finish = () => {
    DONE = true
    void xmpp.stop().catch(() => {})
  }

  xmpp.on("status", (status) => {
    if (DONE) return

    switch (status) {
      case "connecting":
      case "connect":
        return callbacks.onConnStateChange("connecting")
      case "disconnect":
        return callbacks.onConnStateChange("reconnecting")
    }
  })

  xmpp.on("online", async (jid) => {
    callbacks.onConnStateChange("online")

    // An available resource, or the server spools to offline storage and the
    // recipient sees nothing until their next connect.
    await xmpp.send(xml("presence"))

    // Not awaited: it would hold up catch-up, and the user seeing their
    // history, for the whole IQ timeout.
    enableCarbons(xmpp, callbacks.onCarbonsUnavailable)

    await callbacks.onOnline(jid.bare().toString() as Jid)
  })

  // Resumption keeps the session, so nothing re-runs — just say we are back.
  xmpp.streamManagement.on("resumed", () =>
    callbacks.onConnStateChange("online")
  )

  xmpp.on("stanza", (stanza) => routeStanza(stanza, config.jid, callbacks))

  xmpp.on("error", (error: Error & { condition?: string }) => {
    if (error.name === "SASLError") {
      finish()
      callbacks.onAuthFailed()
      return
    }

    if (error.condition && FATAL_STREAM_CONDITIONS.has(error.condition)) {
      reportChatError(`unrecoverable, not retrying: ${error.condition}`, error)
      finish()
      // Terminal, not transient: nothing here retries, so the UI must offer a
      // manual reconnect rather than a "reconnecting…" that never resolves.
      callbacks.onConnStateChange("failed")
      return
    }

    reportChatError("stream error", error)
  })

  xmpp.on("offline", () => {
    if (!DONE) callbacks.onConnStateChange("disconnected")
  })

  return {
    connect: async () => {
      callbacks.onConnStateChange("connecting")
      await xmpp.start()
    },
    disconnect: async () => {
      finish()
      callbacks.onConnStateChange("disconnected")
    },
    sendMessage: async (peer, { id, body, attachment }) => {
      const children = [
        ...(body ? [buildBody(body)] : []),
        ...(attachment ? [buildAttachment(attachment)] : []),
      ]
      await xmpp.send(buildMessage(id, peer, ...children))
    },
    sendShare: async (peer, ref, body) => {
      const id = nanoid()
      const stanza = buildMessage(id, peer, buildBody(body), buildShare(ref))
      await xmpp.send(stanza)
      return id
    },
    retract: (peer, targetId, bounceId) =>
      xmpp.send(buildRetractionMessage(peer, targetId, bounceId)),
    edit: (peer, targetId, body, bounceId) =>
      xmpp.send(buildEditMessage(peer, targetId, body, bounceId)),
    // A lost chat state is not worth reporting: the receiver expires it anyway.
    sendChatState: (peer, state) => {
      xmpp.send(buildChatState(peer, state)).catch(() => {})
    },
    loadHistory: (peer, opts) => loadHistory(xmpp, peer, opts),
    setPushEnabled: (enabled) => setPushEnabled(xmpp, domain, enabled),
  }
}

/**
 * Handles its own failure because there is nothing useful for the caller to
 * await. Reported rather than dropped: without carbons, messages sent from
 * your other tabs stop arriving here, and nothing else would say why.
 */
function enableCarbons(xmpp: Client, onUnavailable?: () => void) {
  xmpp.iqCaller
    .request(
      xml("iq", { type: "set" }, xml("enable", { xmlns: NS_CARBONS })),
      CARBONS_TIMEOUT_MS
    )
    .catch((error: unknown) => {
      reportChatError("carbons off, other tabs will not sync", error)
      onUnavailable?.()
    })
}

// The `jid` is nominal — the PushGateway intercepts before routing — but the
// `node` must be non-empty. Reported on failure, like enableCarbons.
function setPushEnabled(xmpp: Client, domain: string, enabled: boolean) {
  const action = enabled ? "enable" : "disable"
  xmpp.iqCaller
    .request(
      xml(
        "iq",
        { type: "set" },
        xml(action, { xmlns: NS_PUSH, jid: `push.${domain}`, node: PUSH_NODE })
      ),
      PUSH_TIMEOUT_MS
    )
    .catch((error: unknown) => {
      reportChatError(`push ${action} failed`, error)
    })
}
